import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Info,
  Monitor,
  Upload,
  XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  buildBrowserCheckReport,
  runBrowserRequirementChecks,
  type CheckStatus,
} from '../utils/browserCheck';

const statusIcon = (status: CheckStatus) => {
  if (status === 'pass') return <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />;
  if (status === 'fail') return <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />;
  if (status === 'warn') return <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />;
  return <Info className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
};

const BrowserCheckPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const result = useMemo(() => runBrowserRequirementChecks(), []);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropTest, setDropTest] = useState('Not tried yet');
  const [clickTest, setClickTest] = useState('Not tried yet');

  const handleCopyReport = async () => {
    const report = buildBrowserCheckReport(result, { dropTest, clickTest });
    try {
      await navigator.clipboard.writeText(report);
      toast.success('Browser report copied. You can paste it into an email.');
    } catch {
      toast.error('Could not copy automatically. Select the report text and copy it.');
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length === 0) {
      setDropTest('Drop received, but no files. Try dragging from File Explorer, not from email.');
      return;
    }
    setDropTest(`Worked — received ${files.map((file) => file.name).join(', ')}`);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      setClickTest('No file selected');
      return;
    }
    setClickTest(`Worked — selected ${files.map((file) => file.name).join(', ')}`);
  };

  const handleChooseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleDropZoneKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleChooseFiles();
    }
  };

  const overallLabel =
    result.overall === 'pass'
      ? 'This browser looks ready'
      : result.overall === 'warn'
        ? 'This browser should work, with a few caveats'
        : 'This browser does not meet the requirements';

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate('/help')}
          className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
        >
          ← Back to Help
        </button>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <Monitor className="h-6 w-6 text-gray-900 dark:text-gray-100" />
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Browser Check</h1>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Run this on a client machine to see whether the browser is supported. Java is not required.
            </p>
          </div>
          <Button onClick={handleCopyReport} variant="outline">
            <Copy className="h-4 w-4 mr-2" />
            Copy report
          </Button>
        </div>
      </div>

      <Card
        className={clsx(
          'border',
          result.overall === 'pass' && 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20',
          result.overall === 'warn' && 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20',
          result.overall === 'fail' && 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
        )}
      >
        <CardContent className="p-6 flex items-start gap-3">
          {statusIcon(result.overall)}
          <div>
            <p className="font-semibold text-gray-900 dark:text-gray-100">{overallLabel}</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
              {result.browser.name} {result.browser.versionString} · {result.browser.os}
              {result.browser.isMobile ? ' · mobile' : ''}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Requirements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {result.checks.map((check) => (
            <div
              key={check.id}
              className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3"
            >
              {statusIcon(check.status)}
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{check.label}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{check.detail}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">File upload test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Nothing is uploaded. If a file opens in a new tab, it was dropped outside this box — that is normal browser behavior, not a missing plugin.
          </p>
          <div
            role="button"
            tabIndex={0}
            aria-label="Drop a file here to test drag and drop"
            onClick={handleChooseFiles}
            onKeyDown={handleDropZoneKeyDown}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={clsx(
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
              isDragOver
                ? 'border-accent-color bg-accent-color-50 dark:bg-accent-color-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
            )}
          >
            <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-700 dark:text-gray-300">Drop a file here, or click to choose one</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileInputChange}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
              <p className="font-medium text-gray-900 dark:text-gray-100">Drag and drop</p>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{dropTest}</p>
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
              <p className="font-medium text-gray-900 dark:text-gray-100">Choose files</p>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{clickTest}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recommended browsers</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
          <p>Use a current desktop version of Chrome, Microsoft Edge, Firefox, or Safari.</p>
          <p>Internet Explorer and old (non-Chromium) Edge are not supported. Java does not need to be installed.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrowserCheckPage;
